import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Spin, Icon } from './common/Ant'
import { H3, H4 } from './common/Headers'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import { ROLES, SMOOTH_INTERVAL } from '../constants'
import DateRange from './common/DateRange'
import { hasRole } from '../utils/hasPermission'
import { toTitleCase } from '../utils/textFormatters'
import {
  CUSTOM_TIME_RANGE,
  DEFAULT_TIME_RANGE,
  getPresetWindow,
  getCurrentWindow,
  getTimeRangeFromParams,
  getTimeRangeParams,
  getSmoothQuery,
} from '../utils/timeRange'
import Select, { Option } from './common/Select'
import TimeRangeSelect from './common/TimeRangeSelect'
import PressureMonitorChart from './charts/PressureMonitorChart'

const POLL_INTERVAL_MS = 30000

class PressureMonitorData extends Component {
  static propTypes = {
    updateQueryParams: PropTypes.func.isRequired,
    pressureMonitorData: PropTypes.object.isRequired,
    getPressureMonitorData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    pillarId: PropTypes.string.isRequired,
    // in the monitor's measurement units, same as the chart data
    activateThreshold: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    deactivateThreshold: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }

  static defaultProps = {
    pressureMonitorData: {},
    activateThreshold: null,
    deactivateThreshold: null,
  }

  constructor(props) {
    super(props)

    this.state = {
      timeRange: DEFAULT_TIME_RANGE,
      ...getPresetWindow(DEFAULT_TIME_RANGE),
      smoothInterval: SMOOTH_INTERVAL.RAW,
    }
  }

  componentDidMount() {
    const searchParams = parseQueryParams(global.location)
    const { timeRange, ...timeWindow } = getTimeRangeFromParams(searchParams)

    let { smoothInterval } = this.state
    if (searchParams.smoothInterval || searchParams.smooth) {
      smoothInterval = searchParams.smoothInterval
        ? searchParams.smoothInterval
        : !searchParams.smooth || searchParams.smooth === 't'
        ? SMOOTH_INTERVAL.ONE_HOUR
        : SMOOTH_INTERVAL.RAW
    }

    this.setState({ timeRange, ...timeWindow, smoothInterval })
    this.fetchData({ ...timeWindow, smoothInterval })

    this.pollTimer = setInterval(this.poll, POLL_INTERVAL_MS)
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer)
  }

  fetchData = ({ startValue, endValue, smoothInterval }) => {
    const { getPressureMonitorData, pillarId } = this.props
    const qs = createQueryString({
      startTime: startValue.unix(),
      endTime: endValue.unix(),
      ...getSmoothQuery(smoothInterval),
    })

    getPressureMonitorData(pillarId, qs)
  }

  updateUrl = ({ smoothInterval, ...timeRangeState }) => {
    const qs = createQueryString({
      ...getTimeRangeParams(timeRangeState),
      ...getSmoothQuery(smoothInterval),
    })

    this.props.updateQueryParams({
      pathname: global.location.pathname,
      search: qs,
    })
  }

  poll = () => {
    const { timeRange, smoothInterval } = this.state

    if (timeRange === CUSTOM_TIME_RANGE) {
      return
    }

    const timeWindow = getPresetWindow(timeRange)

    this.setState(timeWindow)
    this.fetchData({ ...timeWindow, smoothInterval })
  }

  handleTimeRangeChange = timeRange => {
    const { smoothInterval } = this.state

    // switching to custom starts from whatever window is on screen
    const timeWindow = getCurrentWindow({ ...this.state, timeRange })

    this.setState({ timeRange, ...timeWindow })
    this.updateUrl({ timeRange, ...timeWindow, smoothInterval })

    if (timeRange !== CUSTOM_TIME_RANGE) {
      this.fetchData({ ...timeWindow, smoothInterval })
    }
  }

  onCloseDateRange = (startValue, endValue) => {
    const { timeRange, smoothInterval } = this.state

    if (!startValue?.isValid() || !endValue?.isValid()) {
      console.warn('Invalid date range values received:', {
        startValue,
        endValue,
      })
      return
    }

    this.setState({ startValue, endValue })
    this.updateUrl({ timeRange, startValue, endValue, smoothInterval })
    this.fetchData({ startValue, endValue, smoothInterval })
  }

  handleFilterChange = smoothInterval => {
    const { timeRange } = this.state
    const timeWindow = getCurrentWindow(this.state)

    this.setState({ smoothInterval, ...timeWindow })
    this.updateUrl({ timeRange, ...timeWindow, smoothInterval })
    this.fetchData({ ...timeWindow, smoothInterval })
  }

  render() {
    const {
      isLoading,
      pressureMonitorData,
      activateThreshold,
      deactivateThreshold,
    } = this.props
    const { timeRange, startValue, endValue, smoothInterval } = this.state
    const hasData = pressureMonitorData.data && pressureMonitorData.data.length

    return isLoading && !hasData ? (
      <Spin size="large" className="w-100 center mt5" />
    ) : (
      <Fragment>
        <H3>Historical Data</H3>
        <div className="mb2 flex-ns">
          <TimeRangeSelect
            value={timeRange}
            onChange={this.handleTimeRangeChange}
          />
          {timeRange === CUSTOM_TIME_RANGE && (
            <DateRange
              className="ml4-ns"
              label="Select date range to view Pressure Monitor readings"
              startValue={startValue}
              endValue={endValue}
              onClose={this.onCloseDateRange}
              shouldLimitRange={!hasRole(ROLES.SUPER_ADMIN, ROLES.PILLAR_ADMIN)}
            />
          )}
          <Select
            className="ml4-ns"
            label="Smoothing Interval"
            input={{
              value: smoothInterval,
              onChange: x => this.handleFilterChange(x),
            }}
          >
            {Object.keys(SMOOTH_INTERVAL).map(x => (
              <Option value={SMOOTH_INTERVAL[x]} key={x}>
                {toTitleCase(x)}
              </Option>
            ))}
          </Select>
        </div>
        {hasData && pressureMonitorData.latestElevationCorrected === false && (
          <div
            className="bg-washed-yellow ba b--yellow pa3 br2 mb3 flex items-start"
            role="alert"
          >
            <Icon type="warning" className="yellow f4 mr2" />
            <div>
              <div className="b">Latest reading is not corrected for elevation</div>
              <div>
                The most recent reading in this range was not adjusted for the
                site's elevation, so it may be offset from the true pressure.
                Check that the site's elevation is set and that the monitor has
                received its latest configuration.
              </div>
            </div>
          </div>
        )}
        {hasData ? (
          <PressureMonitorChart
            data={pressureMonitorData.data}
            units={pressureMonitorData.units}
            activateThreshold={activateThreshold}
            deactivateThreshold={deactivateThreshold}
          />
        ) : (
          <H4 className="tc">No Data Available for Selected Date Range</H4>
        )}
      </Fragment>
    )
  }
}

export default PressureMonitorData
